import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Brain, ArrowRight, Layers, Search, Zap, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';

export default function Landing() {
  const { user } = useAuth();

  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center justify-center px-6">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-primary/3 blur-[120px]" />
        </div>

        <motion.div
          className="relative z-10 text-center max-w-2xl mx-auto space-y-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Brain className="h-7 w-7 text-primary" strokeWidth={1.5} />
            </div>
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight">
            MindFlo
          </h1>

          <p className="text-xs uppercase tracking-[0.3em] font-mono text-muted-foreground">
            Human Mind Operating System
          </p>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl mx-auto">
            MindFlo is a private cognitive architecture platform designed to analyze and upgrade the operating system of the human mind.
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-lg mx-auto">
            The system maps patterns across biological stability, neural regulation, cognition, belief structures, and behavior to help individuals understand how their mind actually operates.
          </p>
          <p className="text-xs text-muted-foreground/70 max-w-md mx-auto">
            Access to the platform is currently limited to invited participants working with certified MindFlo counselors.
          </p>

          <div className="flex items-center justify-center gap-4 pt-4">
            <Link to="/login">
              <Button variant="outline" size="lg" data-testid="hero-login-btn">
                Login
              </Button>
            </Link>
            <Link to="/request-access">
              <Button size="lg" className="glow-primary" data-testid="hero-request-btn">
                Request Access
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </motion.div>
      </section>

      {/* What MindFlo Does */}
      <section className="py-24 px-6 border-t border-border/50">
        <div className="max-w-5xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground mb-3">What MindFlo Does</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Layers,
                title: 'Mind Architecture Diagnostics',
                text: 'Understand how biological stability, neural regulation, cognition, beliefs, and behavioral patterns interact inside your mental system.'
              },
              {
                icon: Search,
                title: 'Pattern Mapping',
                text: 'Identify recurring mental loops, hidden belief structures, and sources of cognitive friction.'
              },
              {
                icon: Zap,
                title: 'Upgrade Pathways',
                text: 'Receive structured pathways designed to improve clarity, decision making, and long-term mental performance.'
              }
            ].map((block, i) => (
              <motion.div
                key={block.title}
                className="p-8 rounded-xl bg-card border border-border/50"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <block.icon className="h-8 w-8 text-primary/60 mb-5" strokeWidth={1.5} />
                <h3 className="text-lg font-semibold mb-3">{block.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{block.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Invitation Notice */}
      <section className="py-20 px-6 border-t border-border/50">
        <motion.div
          className="max-w-2xl mx-auto text-center space-y-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 mb-2">
            <Lock className="h-7 w-7 text-primary" strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl font-semibold tracking-tight">Private Platform</h2>
          <p className="text-muted-foreground leading-relaxed">
            MindFlo is currently a private platform. Access is available only through invitation or approval by certified MindFlo counselors.
          </p>
          <Link to="/request-access">
            <Button size="lg" variant="outline" data-testid="notice-request-btn">
              Request Access
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border/50">
        <div className="max-w-5xl mx-auto flex flex-col items-center gap-4">
          <div className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" strokeWidth={1.5} />
            <span className="text-sm font-semibold">MindFlo</span>
          </div>
          <p className="text-xs text-muted-foreground/60">Human Mind Operating System</p>
        </div>
      </footer>
    </div>
  );
}
